import { useEffect, useState, useRef } from "react";
import { api, ROLE_LABELS, formatApiError } from "@/lib/api";
import ClinicMark from "@/components/ClinicMark";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import ReauthModal from "@/components/ReauthModal";
import SchedulingSettings from "@/components/SchedulingSettings";
import LocationSettings from "@/components/LocationSettings";
import {
  Users, Building2, ShieldCheck, Palette, Upload, Loader2, Check, ImageIcon,
  KeyRound, Smartphone, ShieldAlert, AlertTriangle, CalendarCog, Bell, BellOff,
} from "lucide-react";
import { useNotifications } from "@/context/NotificationContext";

// Locations is the one piece of location UI that is always visible. Progressive
// disclosure hides pickers and filters from single-site clinics — but hiding the screen
// where you ADD a second site would make the second site unreachable.
const TABS = [
  { k: "branding", label: "Branding", icon: Palette },
  { k: "scheduling", label: "Scheduling", icon: CalendarCog },
  { k: "locations", label: "Locations", icon: Building2 },
  { k: "team", label: "Team", icon: Users },
  { k: "notifications", label: "Notifications", icon: Bell },
  { k: "security", label: "Security", icon: ShieldCheck },
];

function BrandingTab() {
  const { user } = useAuth();
  const { clinic, updateClinic } = useClinic();
  const canEdit = ["owner", "system_admin"].includes(user?.role);
  const fileRef = useRef(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (clinic && !form) {
      setForm({
        name: clinic.name || "",
        tagline: clinic.tagline || "",
        logo_url: clinic.logo_url || "",
        brand_color: clinic.brand_color || "#1E293B",
        welcome_message: clinic.welcome_message || "",
        support_email: clinic.support_email || "",
        programs: clinic.programs || [],
        renewal_allow_date_change: clinic.renewal_allow_date_change !== false,
        renewal_reminder_days: clinic.renewal_reminder_days || 5,
      });
    }
  }, [clinic, form]);

  if (!form) return <div className="text-sm text-muted-foreground">Loading branding…</div>;

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setSaved(false); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_000_000) { setError("Logo must be under 1MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm({ ...form, logo_url: reader.result }); setSaved(false); setError(""); };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put("/clinic", form);
      updateClinic(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="branding-tab">
      {/* Editor */}
      <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6 space-y-5">
        <div>
          <h3 className="font-heading text-lg font-semibold">White-label Branding</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Make the app feel like your clinic's own.</p>
        </div>

        {!canEdit && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Only the Clinic Owner can edit branding. Viewing in read-only mode.
          </p>
        )}

        {/* Logo */}
        <div>
          <label className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Logo</label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-secondary flex items-center justify-center overflow-hidden border border-border">
              {form.logo_url ? <img src={form.logo_url} alt="logo" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-muted-foreground" />}
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-file-input" />
                <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-sm font-medium border border-border rounded-md px-3 py-2 hover:bg-secondary" data-testid="upload-logo-button">
                  <Upload className="w-4 h-4" /> Upload
                </button>
                {form.logo_url && (
                  <button onClick={() => { setForm({ ...form, logo_url: "" }); setSaved(false); }} className="text-sm font-medium text-muted-foreground hover:text-red-600 px-2" data-testid="remove-logo-button">Remove</button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <L label="Clinic name">
            <input data-testid="branding-name" disabled={!canEdit} value={form.name} onChange={set("name")} className="binput" />
          </L>
          <L label="Tagline">
            <input data-testid="branding-tagline" disabled={!canEdit} value={form.tagline} onChange={set("tagline")} className="binput" />
          </L>
        </div>

        <L label="Brand color">
          <div className="flex items-center gap-3">
            <input type="color" disabled={!canEdit} value={form.brand_color} onChange={set("brand_color")} className="h-10 w-14 rounded-md border border-border cursor-pointer disabled:cursor-not-allowed" data-testid="branding-color" />
            <input disabled={!canEdit} value={form.brand_color} onChange={set("brand_color")} className="binput max-w-[140px] font-mono" />
          </div>
        </L>

        <L label="Welcome message">
          <textarea data-testid="branding-welcome" disabled={!canEdit} value={form.welcome_message} onChange={set("welcome_message")} rows={2} className="binput" />
        </L>

        <L label="Support email">
          <input data-testid="branding-support-email" disabled={!canEdit} value={form.support_email} onChange={set("support_email")} className="binput" />
        </L>

        <div className="pt-2 border-t border-border">
          <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground mb-3">Smart Renewal</p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="branding-allow-date-change"
              disabled={!canEdit}
              checked={!!form.renewal_allow_date_change}
              onChange={(e) => { setForm({ ...form, renewal_allow_date_change: e.target.checked }); setSaved(false); }}
              className="w-4 h-4 rounded border-input"
            />
            <span className="text-sm">Let patients request a different renewal date</span>
          </label>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Remind patients</span>
            <input
              type="number" min="1" max="14"
              data-testid="branding-reminder-days"
              disabled={!canEdit}
              value={form.renewal_reminder_days}
              onChange={(e) => { setForm({ ...form, renewal_reminder_days: parseInt(e.target.value) || 5 }); setSaved(false); }}
              className="binput max-w-[70px] text-center"
            />
            <span className="text-sm text-muted-foreground">days before renewal</span>
          </div>
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}

        {canEdit && (
          <div className="flex items-center gap-3 pt-1">
            <button onClick={save} disabled={saving} data-testid="save-branding-button" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
              {saved ? "Saved" : "Save branding"}
            </button>
            {saved && <span className="text-sm text-emerald-700">Applied across the app.</span>}
          </div>
        )}
        <style>{`.binput{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));border-radius:0.375rem;padding:0.55rem 0.75rem;font-size:0.875rem;outline:none}.binput:focus{box-shadow:0 0 0 2px hsl(var(--ring))}.binput:disabled{background:hsl(var(--secondary));color:hsl(var(--muted-foreground))}`}</style>
      </div>

      {/* Live preview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground mb-3">Live Preview</p>
        <div className="rounded-lg overflow-hidden border border-border" data-testid="branding-preview">
          <div className="p-4 text-white" style={{ background: form.brand_color }}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center overflow-hidden">
                {/* Reflects unsaved edits, so it takes the form's values, not context. */}
                <ClinicMark name={form.name} logoUrl={form.logo_url} className="text-sm" />
              </div>
              <div className="min-w-0">
                <p className="font-heading font-bold text-sm leading-tight truncate">{form.name || "Your Clinic"}</p>
                <p className="text-[11px] text-white/60 truncate">{form.tagline || "Tagline"}</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-background">
            <p className="text-sm font-medium">{form.welcome_message || "Welcome message preview"}</p>
            <button className="mt-3 text-xs font-semibold text-white rounded-md px-3 py-2" style={{ background: form.brand_color }}>
              Primary button
            </button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Patients see your brand. RelationshipOS stays behind the scenes.</p>
      </div>
    </div>
  );
}

function TeamTab() {
  const [staff, setStaff] = useState([]);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="team-tab">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-primary" />
        <h3 className="font-heading text-lg font-semibold">Team Members</h3>
      </div>
      <div className="divide-y divide-border" data-testid="staff-list">
        {staff.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-sm font-semibold font-heading">
                {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </div>
              <div>
                <p className="text-sm font-medium">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.email}</p>
              </div>
            </div>
            <span className="text-xs font-medium bg-secondary text-secondary-foreground rounded-full px-3 py-1">
              {ROLE_LABELS[s.role] || s.role}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MfaCard() {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null); // {secret, qr_data_url}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [reauth, setReauth] = useState(false);
  const [disarming, setDisarming] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const load = () => api.get("/auth/mfa/status").then((r) => setEnabled(r.data.mfa_enabled)).catch(() => setEnabled(false));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const enable = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/mfa/enable", { code });
      setSetup(null); setCode(""); setMsg("Two-factor authentication is now enabled.");
      setEnabled(true);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const doDisable = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/mfa/disable", { code: disableCode });
      setDisarming(false); setDisableCode(""); setMsg("Two-factor authentication disabled.");
      setEnabled(false);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); setDisableCode(""); }
    setBusy(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="mfa-card">
      <ReauthModal open={reauth} onClose={() => setReauth(false)} onSuccess={() => { setReauth(false); setDisarming(true); }} title="Confirm to disable 2FA" />
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-5 h-5 text-primary" />
        <h3 className="font-heading text-lg font-semibold">Two-Factor Authentication</h3>
        {enabled && <span className="ml-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5" data-testid="mfa-enabled-badge">Enabled</span>}
      </div>
      <p className="text-sm text-muted-foreground mb-4">Add a one-time code from an authenticator app for an extra layer of account security.</p>

      {msg && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mb-3">{msg}</p>}
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3" data-testid="mfa-error">{error}</p>}

      {enabled === false && !setup && (
        <button onClick={startSetup} disabled={busy} data-testid="mfa-setup-button" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />} Set up two-factor
        </button>
      )}

      {setup && (
        <div className="space-y-4" data-testid="mfa-setup-panel">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <img src={setup.qr_data_url} alt="MFA QR code" className="w-40 h-40 rounded-lg border border-border" data-testid="mfa-qr" />
            <div className="text-sm text-muted-foreground space-y-2">
              <p>1. Scan this QR code with Google Authenticator, Authy, or 1Password.</p>
              <p>2. Or enter this key manually:</p>
              <code className="block bg-secondary rounded px-2 py-1 font-mono text-xs break-all" data-testid="mfa-secret">{setup.secret}</code>
              <p>3. Enter the 6-digit code below to confirm.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="mfa-enable-code"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="rounded-md border border-input bg-card px-3 py-2.5 text-base tracking-[0.3em] text-center font-mono w-40 outline-none focus:ring-2 focus:ring-ring"
            />
            <button onClick={enable} disabled={busy || code.length !== 6} data-testid="mfa-enable-button" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirm & enable
            </button>
            <button onClick={() => { setSetup(null); setCode(""); }} className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
          </div>
        </div>
      )}

      {enabled && !disarming && (
        <button onClick={() => setReauth(true)} data-testid="mfa-disable-button" className="inline-flex items-center gap-2 border border-border rounded-md px-4 py-2.5 text-sm font-medium hover:bg-secondary text-red-600">
          Disable two-factor
        </button>
      )}

      {enabled && disarming && (
        <div className="flex items-center gap-2" data-testid="mfa-disable-panel">
          <input
            data-testid="mfa-disable-code"
            inputMode="numeric"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Current code"
            className="rounded-md border border-input bg-card px-3 py-2.5 text-base tracking-[0.2em] text-center font-mono w-40 outline-none focus:ring-2 focus:ring-ring"
          />
          <button onClick={doDisable} disabled={busy || disableCode.length !== 6} className="inline-flex items-center gap-2 bg-red-600 text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-red-700 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirm disable
          </button>
          <button onClick={() => { setDisarming(false); setDisableCode(""); }} className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
        </div>
      )}
    </div>
  );
}

function BreakGlassCard() {
  const [sessions, setSessions] = useState([]);
  const [reauth, setReauth] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/auth/break-glass").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/break-glass", { reason });
      setShowForm(false); setReason(""); load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const end = async (id) => { await api.post(`/auth/break-glass/${id}/end`).catch(() => {}); load(); };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="break-glass-card">
      <ReauthModal open={reauth} onClose={() => setReauth(false)} onSuccess={() => { setReauth(false); setShowForm(true); }} title="Confirm for emergency access" />
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="w-5 h-5 text-amber-600" />
        <h3 className="font-heading text-lg font-semibold">Break-glass Emergency Access</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">For urgent situations requiring access beyond normal limits. Every use requires a reason, is time-limited to one hour, and is heavily audited.</p>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3" data-testid="bg-error">{error}</p>}

      {!showForm ? (
        <button onClick={() => setReauth(true)} data-testid="break-glass-open-button" className="inline-flex items-center gap-2 border border-amber-300 text-amber-800 bg-amber-50 rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-amber-100">
          <AlertTriangle className="w-4 h-4" /> Open emergency access
        </button>
      ) : (
        <div className="space-y-3" data-testid="break-glass-form">
          <textarea
            data-testid="break-glass-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason for emergency access (required, min 8 characters)…"
            className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="flex items-center gap-2">
            <button onClick={open} disabled={busy || reason.trim().length < 8} data-testid="break-glass-confirm-button" className="inline-flex items-center gap-2 bg-amber-600 text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-amber-700 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Grant emergency access
            </button>
            <button onClick={() => { setShowForm(false); setReason(""); }} className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
          </div>
        </div>
      )}

      {sessions.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border" data-testid="break-glass-list">
          <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground mb-2">Recent emergency access</p>
          <div className="divide-y divide-border">
            {sessions.slice(0, 5).map((s) => (
              <div key={s.id} className="flex items-start justify-between py-2.5 gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{s.reason}</p>
                  <p className="text-xs text-muted-foreground">{s.user_name} · {new Date(s.started_at).toLocaleString()}</p>
                </div>
                {s.active ? (
                  <button onClick={() => end(s.id)} data-testid={`break-glass-end-${s.id}`} className="shrink-0 text-xs font-medium border border-border rounded-md px-2.5 py-1 hover:bg-secondary">End now</button>
                ) : (
                  <span className="shrink-0 text-xs text-muted-foreground">Closed</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Per-user notification settings.
 *
 *  Two separate things, and the screen says which is which, because the distinction is
 *  the whole reason one of them is opt-in:
 *
 *    - In-app toasts appear inside this portal, on a screen somebody is signed in to.
 *      They name the patient, because that is what makes them useful.
 *    - Desktop notifications appear OUTSIDE it — on a lock screen, on a shared
 *      workstation, in a notification centre that outlives the session. They say only
 *      that something is waiting, and the exact words are shown below so nobody has to
 *      turn it on to find out what it will say.
 */
function NotificationsTab() {
  const { prefs, savePrefs, requestOsPermission } = useNotifications();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [permission, setPermission] = useState(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission);

  const supported = permission !== "unsupported";
  const on = Boolean(prefs?.os_notifications);

  const toggle = async () => {
    setBusy(true);
    setError("");
    try {
      if (!on) {
        // Permission is requested HERE, at the moment somebody asks for the feature —
        // never at first load. An unexplained prompt is the one everybody denies, and a
        // denial is sticky: the browser will not ask again.
        const result = await requestOsPermission();
        setPermission(result);
        if (result !== "granted") {
          setError(result === "denied"
            ? "Your browser is blocking notifications for this site. Allow them in the address bar, then try again."
            : "Your browser did not grant permission, so nothing was turned on.");
          setBusy(false);
          return;
        }
      }
      await savePrefs({ os_notifications: !on });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "That setting was not saved.");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="notifications-tab">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-5 h-5 text-sky-600" />
          <h3 className="font-heading text-lg font-semibold">In-app alerts</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Always on. A toast appears in this portal when a patient messages, when a patient
          arrives for a visit you are seeing, and when a colleague messages you. The Messages
          count in the sidebar and the browser tab title follow the same feed.
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          {on ? <Bell className="w-5 h-5 text-sky-600" /> : <BellOff className="w-5 h-5 text-muted-foreground" />}
          <h3 className="font-heading text-lg font-semibold">Desktop notifications</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Off unless you turn them on, and this setting is yours alone — it follows you to any
          workstation you sign in at.
        </p>

        <div className="mt-4 rounded-md border border-border bg-secondary/40 p-4">
          <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">
            Exactly what a desktop notification says
          </p>
          <ul className="mt-2 space-y-1 text-sm" data-testid="os-notification-wording">
            <li>· <span className="font-medium">New patient message</span></li>
            <li>· <span className="font-medium">A patient has arrived</span></li>
            <li>· <span className="font-medium">New staff message</span></li>
          </ul>
          <p className="text-xs text-muted-foreground mt-3">
            Plus your clinic's name. Never a patient's name, never what the message said, never
            the kind of visit — these land on lock screens and shared desks, and whoever is
            looking at the screen is not always you.
          </p>
        </div>

        {error && (
          <p className="mt-3 text-sm text-destructive" data-testid="notifications-error">{error}</p>
        )}

        {!supported ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="notifications-unsupported">
            This browser does not support desktop notifications. In-app alerts still work.
          </p>
        ) : (
          <button
            data-testid="toggle-os-notifications"
            onClick={toggle}
            disabled={busy}
            aria-pressed={on}
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : on ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            {on ? "Turn off desktop notifications" : "Turn on desktop notifications"}
          </button>
        )}
        <p className="mt-2 text-xs text-muted-foreground" data-testid="os-notification-state">
          Currently {on ? "on" : "off"}
          {supported && permission === "denied" && " · your browser is blocking them"}
        </p>
      </div>
    </div>
  );
}


function SecurityTab() {
  const { user } = useAuth();
  const isAdmin = ["owner", "system_admin"].includes(user?.role);
  return (
    <div className="space-y-6" data-testid="security-tab">
      <MfaCard />
      {isAdmin && <BreakGlassCard />}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-semibold">Security & Compliance</h3>
        </div>
        <ul className="grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
          <li>• Role-based access control (least privilege)</li>
          <li>• Encrypted password storage (bcrypt)</li>
          <li>• Two-factor authentication (TOTP) for staff</li>
          <li>• Short-lived tokens with refresh rotation & revocation</li>
          <li>• Automatic logoff after inactivity</li>
          <li>• Step-up re-authentication for sensitive actions</li>
          <li>• Tamper-evident, hash-chained audit log</li>
          <li>• Break-glass emergency access (time-limited, audited)</li>
          <li>• Brute-force login protection</li>
          <li>• Strict tenant isolation · no PHI in logs</li>
        </ul>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function Settings() {
  const { clinic } = useClinic();
  const [tab, setTab] = useState("branding");

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">{clinic?.name || "Your clinic"} configuration.</p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`settings-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "branding" && <BrandingTab />}
      {tab === "scheduling" && <SchedulingSettings />}
      {tab === "locations" && <LocationSettings />}
      {tab === "team" && <TeamTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "security" && <SecurityTab />}
    </div>
  );
}
